/**
 * KPI / indicator card — one headline number from a layer.
 *
 * The statistic is computed by the service where possible (ArcGIS) or from a
 * capped record read (GeoServer); a partial result is labelled rather than
 * presented as the true total.
 */

import { useSourceFields } from './useSourceFields';
import { useWidgetData } from './useWidgetData';
import WidgetFrame from './WidgetFrame';
import { combineWhere, useIncomingConstraint, type ActionTargetProps } from './useActions';

import { getDataSourceAdapter, type StatisticResult } from '../services/dashboardDataService';
import { buildWhereClause, formatCompact, validateWidget } from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardWidget } from '../types/dashboardTypes';
import { STATISTIC_LABELS } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  source?: DashboardDataSource;
  refreshInterval: number;
  accentColor: string;
  dividerColor?: string;
  /** WHERE contributed by the header's own selectors — see `selectorRuntime.ts`. */
  selectorWhere?: string;
}

type AllProps = Props & ActionTargetProps;

export default function IndicatorCard({
  widget,
  source,
  refreshInterval,
  accentColor,
  dividerColor,
  selectorWhere,
  incomingSignals,
  onClearSignals,
}: AllProps) {
  const { fields } = useSourceFields(source);
  const problems = validateWidget(widget);

  const statistic = widget.config.statistic ?? 'count';
  // The widget's own filter, whatever another widget is imposing on it, AND
  // whatever the header's selectors are narrowing this data source to.
  const incoming = useIncomingConstraint(incomingSignals, fields);
  const where = combineWhere(combineWhere(buildWhereClause(widget.filters, fields), incoming.where), selectorWhere ?? '');
  const enabled = Boolean(source) && problems.length === 0;

  const { data, status, error, reload } = useWidgetData<StatisticResult>(
    () =>
      getDataSourceAdapter(source as DashboardDataSource).fetchStatistic(
        source as DashboardDataSource,
        { statistic, field: widget.config.field, where, extent: incoming.extent, geometry: incoming.geometry },
      ),
    `kpi|${source?.id}|${statistic}|${widget.config.field ?? ''}|${where}|${JSON.stringify(incoming.extent ?? null)}|${incoming.geometry ? 'geom' : ''}`,
    refreshInterval,
    enabled,
  );

  const color = widget.config.color || accentColor;
  const value = data?.value ?? null;

  const footnoteParts = [
    source ? source.name : '',
    statistic === 'count' ? 'Count' : `${STATISTIC_LABELS[statistic]} of ${widget.config.field}`,
    data?.truncated ? `partial — first 5,000 records` : '',
  ].filter(Boolean);

  return (
    <WidgetFrame
      title={widget.title}
      status={status}
      error={error}
      onRetry={reload}
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      footnote={footnoteParts.join(' · ')}
      actionLabels={incoming.labels}
      onClearActions={() => onClearSignals?.(incoming.sourceIds)}
    >
      <div className="flex h-full flex-col items-center justify-center px-3 text-center">
        <p
          className="w-full truncate text-4xl font-semibold leading-none tracking-tight"
          style={{ color }}
          title={value === null ? undefined : String(value)}
        >
          {widget.config.prefix}
          {formatCompact(value, widget.config.decimals ?? 0)}
          {widget.config.suffix}
        </p>
        {widget.config.caption && (
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">{widget.config.caption}</p>
        )}
      </div>
    </WidgetFrame>
  );
}
